import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Save, Trash2, Copy, AlertTriangle, CheckCircle, Clock, 
  ChevronDown, ChevronUp, Edit2, X, RefreshCw, AlertCircle,
  TrendingDown, Activity, FileText, Pill, Leaf, Utensils,
  Table2, Calendar, User, ArrowRight, Zap, XCircle
} from 'lucide-react';

const API = '/api';

const TIMING_BLOCKS = ["wake", "morning", "mid_morning", "lunch", "afternoon", "evening", "dinner", "night", "sleep"];
const EVENT_TYPES = ["lifeflo", "regenflo", "medication", "food", "therapy", "assessment"];

const BLOCK_LABELS = {
  wake: 'Wake', morning: 'Morning', mid_morning: 'Mid-Morning', lunch: 'Lunch',
  afternoon: 'Afternoon', evening: 'Evening', dinner: 'Dinner', night: 'Night', sleep: 'Sleep',
  anytime: 'Flexible / Anytime'
};

const EVENT_TYPE_STYLES = {
  lifeflo:    { icon: Leaf,     bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  regenflo:   { icon: Activity, bg: 'bg-blue-500/10',    text: 'text-blue-400',    border: 'border-blue-500/20' },
  medication: { icon: Pill,     bg: 'bg-amber-500/10',   text: 'text-amber-400',   border: 'border-amber-500/20' },
  food:       { icon: Utensils, bg: 'bg-rose-500/10',    text: 'text-rose-400',    border: 'border-rose-500/20' },
  therapy:    { icon: Activity, bg: 'bg-purple-500/10',  text: 'text-purple-400',  border: 'border-purple-500/20' },
  assessment: { icon: FileText, bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    border: 'border-cyan-500/20' },
};

const STATUS_CONFIG = {
  as_scheduled: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'As Scheduled', icon: CheckCircle },
  changed:      { color: 'text-amber-400',   bg: 'bg-amber-500/10',   label: 'Changed',      icon: AlertTriangle },
  skipped:      { color: 'text-red-400',     bg: 'bg-red-500/10',     label: 'Skipped',      icon: XCircle },
  pending:      { color: 'text-white/30',    bg: 'bg-adm-surface/5',        label: 'Pending',      icon: Clock },
};

// ══════════════════════════════════════════════════════════════════════════════
// TABLE EDITOR COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

function ItemRow({ item, onEdit, onDuplicate, onDelete, onToggleActive }) {
  const style = EVENT_TYPE_STYLES[item.event_type] || EVENT_TYPE_STYLES.regenflo;
  const Icon = style.icon;
  
  return (
    <tr className={`border-b border-white/[0.03] hover:bg-adm-surface/[0.02] ${!item.is_active ? 'opacity-40' : ''}`} data-testid={`item-row-${item.id}`}>
      <td className="px-2 py-2 text-xs text-white/30 font-mono">{item.sort_order}</td>
      <td className="px-2 py-2 text-xs font-mono text-white/60">{item.planned_time}</td>
      <td className="px-2 py-2">
        <Badge className={`${style.bg} ${style.text} ${style.border} text-[9px] px-1.5 py-0.5 border`}>
          <Icon className="w-2.5 h-2.5 mr-0.5" />
          {item.event_type}
        </Badge>
      </td>
      <td className="px-2 py-2 text-xs font-mono text-white/50">{item.code}</td>
      <td className="px-2 py-2 text-xs text-white/80 max-w-[160px] truncate">{item.name}</td>
      <td className="px-2 py-2 text-xs text-white/50 max-w-[100px] truncate">{item.planned_detail_dose}</td>
      <td className="px-2 py-2 text-xs text-white/40 max-w-[120px] truncate">{item.instruction}</td>
      <td className="px-2 py-2 text-xs text-white/40">{item.context}</td>
      <td className="px-2 py-2 text-xs text-white/30">{item.timing_block}</td>
      <td className="px-2 py-2 text-xs text-white/30">{item.display_group_key}</td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => onEdit(item)} className="h-6 w-6 p-0 text-white/40 hover:text-white">
            <Edit2 className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDuplicate(item.id)} className="h-6 w-6 p-0 text-white/40 hover:text-emerald-400">
            <Copy className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onToggleActive(item)} className="h-6 w-6 p-0 text-white/40 hover:text-amber-400">
            {item.is_active ? <X className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(item.id)} className="h-6 w-6 p-0 text-white/40 hover:text-red-400">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function ItemEditDialog({ item, open, onClose, onSave }) {
  const [form, setForm] = useState(item || {});
  
  useEffect(() => {
    if (item) setForm(item);
  }, [item]);
  
  const handleSave = () => {
    onSave(form);
    onClose();
  };
  
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item?.id ? 'Edit Schedule Item' : 'Add Schedule Item'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-4">
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Planned Time</label>
            <Input type="time" value={form.planned_time || ''} onChange={e => setForm({...form, planned_time: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Event Type</label>
            <Select value={form.event_type || ''} onValueChange={v => setForm({...form, event_type: v})}>
              <SelectTrigger className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {EVENT_TYPES.map(t => <SelectItem key={t} value={t} className="text-white text-xs">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Code</label>
            <Input value={form.code || ''} onChange={e => setForm({...form, code: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="P1-M01" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Name</label>
            <Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Levodopa" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Dose/Detail</label>
            <Input value={form.planned_detail_dose || ''} onChange={e => setForm({...form, planned_detail_dose: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="250mg" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Timing Block</label>
            <Select value={form.timing_block || ''} onValueChange={v => setForm({...form, timing_block: v})}>
              <SelectTrigger className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs">
                <SelectValue placeholder="Select block" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {TIMING_BLOCKS.map(b => <SelectItem key={b} value={b} className="text-white text-xs">{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-white/40 mb-1 block">Instruction</label>
            <Input value={form.instruction || ''} onChange={e => setForm({...form, instruction: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Take with water" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Context</label>
            <Input value={form.context || ''} onChange={e => setForm({...form, context: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="before_food" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Display Group</label>
            <Input value={form.display_group_key || ''} onChange={e => setForm({...form, display_group_key: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="morning_meds" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-white/40 mb-1 block">Display Group Label</label>
            <Input value={form.display_group_label || ''} onChange={e => setForm({...form, display_group_label: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Morning Medications" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-xs text-white/50">Cancel</Button>
          <Button onClick={handleSave} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ValidationPanel({ validation }) {
  if (!validation) return null;
  
  return (
    <div className="mb-4 space-y-2">
      {validation.errors?.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-400 text-xs mb-2">
            <AlertTriangle className="w-4 h-4" /> Validation Errors
          </div>
          {validation.errors.map((e, i) => (
            <div key={i} className="text-xs text-red-300/70 ml-6">• {e.message} {e.code && `(${e.code})`}</div>
          ))}
        </div>
      )}
      {validation.warnings?.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 text-amber-400 text-xs mb-2">
            <AlertCircle className="w-4 h-4" /> Warnings
          </div>
          {validation.warnings.map((w, i) => (
            <div key={i} className="text-xs text-amber-300/70 ml-6">• {w.message}</div>
          ))}
        </div>
      )}
      {validation.valid && validation.errors?.length === 0 && validation.warnings?.length === 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 text-emerald-400 text-xs">
            <CheckCircle className="w-4 h-4" /> Schedule is valid with no conflicts
          </div>
        </div>
      )}
    </div>
  );
}

function FrictionPanel({ friction }) {
  if (!friction || friction.total_logs === 0) return null;
  
  return (
    <Card className="bg-adm-surface/[0.02] border-white/[0.06] mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white/70 flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-amber-400" /> Execution Friction Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-white/40 mb-1">Most Skipped</div>
            {friction.most_skipped?.slice(0, 3).map((item, i) => (
              <div key={i} className="text-white/60">{item.code || item.name}: <span className="text-red-400">{item.skip_rate}%</span></div>
            ))}
          </div>
          <div>
            <div className="text-white/40 mb-1">Most Changed</div>
            {friction.most_changed?.slice(0, 3).map((item, i) => (
              <div key={i} className="text-white/60">{item.code || item.name}: <span className="text-amber-400">{item.change_rate}%</span></div>
            ))}
          </div>
          <div>
            <div className="text-white/40 mb-1">Unstable Blocks</div>
            {friction.unstable_blocks?.slice(0, 3).map((block, i) => (
              <div key={i} className="text-white/60">{block.block}: <span className="text-amber-400">{block.instability_rate}%</span></div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TIMELINE VIEW COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

function SummaryStrip({ summary }) {
  return (
    <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-4 mb-4">
      <div className="grid grid-cols-7 gap-4">
        <div className="text-center">
          <div className="text-xl font-bold text-white/90">{summary.total_events}</div>
          <div className="text-[10px] text-white/40 mt-1">Total</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-emerald-400">{summary.as_scheduled}</div>
          <div className="text-[10px] text-white/40 mt-1">On Time</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-amber-400">{summary.changed}</div>
          <div className="text-[10px] text-white/40 mt-1">Changed</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-red-400">{summary.skipped}</div>
          <div className="text-[10px] text-white/40 mt-1">Skipped</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-white/50">{summary.pending}</div>
          <div className="text-[10px] text-white/40 mt-1">Pending</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-blue-400">{summary.exact_timing_pct}%</div>
          <div className="text-[10px] text-white/40 mt-1">Exact</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-bold text-rose-400">{summary.main_friction_zone || '—'}</div>
          <div className="text-[10px] text-white/40 mt-1">Friction Zone</div>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({ item, ruleViolations }) {
  const typeConfig = EVENT_TYPE_STYLES[item.event_type] || EVENT_TYPE_STYLES.regenflo;
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;
  
  const itemViolations = ruleViolations?.filter(v => v.items?.includes(item.id)) || [];
  const hasRed = itemViolations.some(v => v.severity === 'red');
  const hasYellow = itemViolations.some(v => v.severity === 'yellow');
  const borderColor = hasRed ? 'border-red-500/40' : hasYellow ? 'border-amber-500/40' : 'border-white/[0.06]';
  
  return (
    <div className={`bg-adm-surface/[0.02] border ${borderColor} rounded-lg p-3 mb-2`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-16 text-right">
            <div className="text-xs font-mono text-white/40">{item.planned_time}</div>
            {item.actual_time && item.actual_time !== item.planned_time && (
              <div className="flex items-center justify-end gap-1 mt-0.5">
                <ArrowRight className="w-2 h-2 text-amber-400" />
                <span className="text-xs font-mono text-amber-400">{item.actual_time}</span>
              </div>
            )}
          </div>
          <div className={`w-6 h-6 rounded ${typeConfig.bg} flex items-center justify-center`}>
            <TypeIcon className={`w-3.5 h-3.5 ${typeConfig.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-white/40">{item.code}</span>
              <span className="text-sm text-white/90 truncate">{item.name}</span>
            </div>
            {item.planned_dose && (
              <div className="text-[10px] text-white/40 mt-0.5">{item.planned_dose}</div>
            )}
          </div>
        </div>
        <Badge className={`${statusConfig.bg} ${statusConfig.color} border-0 text-[9px] flex items-center gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {statusConfig.label}
        </Badge>
      </div>
    </div>
  );
}

function TimelineBlock({ block, ruleViolations }) {
  const [expanded, setExpanded] = useState(true);
  const totalItems = block.summary?.total || 0;
  const isUnstable = block.instability_rate > 20;
  
  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
          isUnstable ? 'bg-red-500/5 border-red-500/20' : 'bg-adm-surface/[0.02] border-white/[0.06]'
        } hover:bg-adm-surface/[0.04]`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white/80">{BLOCK_LABELS[block.block] || block.block}</span>
          <Badge className="text-[9px] bg-adm-surface/5 text-white/40 border-0">{totalItems} items</Badge>
          {block.summary && (
            <div className="flex items-center gap-1 text-[10px]">
              <span className="text-emerald-400">{block.summary.as_scheduled} ✓</span>
              <span className="text-amber-400">{block.summary.changed} Δ</span>
              <span className="text-red-400">{block.summary.skipped} ✗</span>
            </div>
          )}
          {isUnstable && (
            <Badge className="text-[9px] bg-red-500/10 text-red-400 border-0">
              <TrendingDown className="w-3 h-3 mr-1" /> {block.instability_rate}%
            </Badge>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
      </button>
      {expanded && (
        <div className="mt-2 pl-4 border-l-2 border-white/[0.06]">
          {block.grouped_items?.map(group => (
            <div key={group.group_key} className="mb-2">
              <div className="text-[10px] text-white/30 mb-1 flex items-center gap-2">
                <span className="font-mono">{group.planned_time}</span>
                <span>{group.group_label}</span>
              </div>
              {group.items.map(item => (
                <TimelineItem key={item.id} item={item} ruleViolations={ruleViolations} />
              ))}
            </div>
          ))}
          {block.ungrouped_items?.map(item => (
            <TimelineItem key={item.id} item={item} ruleViolations={ruleViolations} />
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function ScheduleEditor() {
  const [activeTab, setActiveTab] = useState('editor');
  const [phases, setPhases] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState(null);
  const [friction, setFriction] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Timeline state
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  // Fetch phases
  useEffect(() => {
    fetch(`${API}/admin/v3/phases`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.phases) {
          setPhases(d.phases);
          const active = d.phases.find(p => p.status === 'active');
          if (active) setSelectedPhase(active);
        }
      })
      .finally(() => setLoading(false));
  }, []);
  
  // Fetch subscribers for timeline
  useEffect(() => {
    fetch(`${API}/admin/v3/subscribers-list`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.subscribers) {
          setSubscribers(d.subscribers);
          const withPhase = d.subscribers.find(s => s.has_active_phase);
          if (withPhase) setSelectedSubscriber(withPhase.id);
        }
      });
  }, []);
  
  // Fetch items for selected phase
  const fetchItems = useCallback(async () => {
    if (!selectedPhase) return;
    const res = await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/items`, { headers });
    if (res.ok) {
      const data = await res.json();
      setItems(data.items || []);
    }
  }, [selectedPhase]);
  
  const fetchValidation = useCallback(async () => {
    if (!selectedPhase) return;
    const res = await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/validate`, { headers });
    if (res.ok) setValidation(await res.json());
  }, [selectedPhase]);
  
  const fetchFriction = useCallback(async () => {
    if (!selectedPhase) return;
    const res = await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/friction-analysis`, { headers });
    if (res.ok) setFriction(await res.json());
  }, [selectedPhase]);
  
  useEffect(() => {
    if (selectedPhase) {
      fetchItems();
      fetchValidation();
      fetchFriction();
    }
  }, [selectedPhase, fetchItems, fetchValidation, fetchFriction]);
  
  // Fetch timeline data
  const fetchTimeline = useCallback(async () => {
    if (!selectedSubscriber || !selectedDate) return;
    setTimelineLoading(true);
    const res = await fetch(`${API}/admin/v3/timeline-review/${selectedSubscriber}?date=${selectedDate}`, { headers });
    if (res.ok) setTimelineData(await res.json());
    setTimelineLoading(false);
  }, [selectedSubscriber, selectedDate]);
  
  useEffect(() => {
    if (activeTab === 'timeline') fetchTimeline();
  }, [activeTab, fetchTimeline]);
  
  // Editor actions
  const handleAddItem = () => {
    setEditItem({ 
      planned_time: '', event_type: 'regenflo', code: '', name: '', 
      planned_detail_dose: '', instruction: '', context: '', 
      timing_block: 'morning', display_group_key: '', display_group_label: ''
    });
    setShowEditDialog(true);
  };
  
  const handleEdit = (item) => {
    setEditItem(item);
    setShowEditDialog(true);
  };
  
  const handleSaveItem = async (formData) => {
    if (!selectedPhase) return;
    if (formData.id) {
      await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/items/${formData.id}`, {
        method: 'PUT', headers, body: JSON.stringify(formData)
      });
    } else {
      await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/items`, {
        method: 'POST', headers, body: JSON.stringify(formData)
      });
    }
    fetchItems();
    fetchValidation();
  };
  
  const handleDuplicate = async (itemId) => {
    await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/items/${itemId}/duplicate`, { method: 'POST', headers });
    fetchItems();
  };
  
  const handleToggleActive = async (item) => {
    await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/items/${item.id}`, {
      method: 'PUT', headers, body: JSON.stringify({ is_active: !item.is_active })
    });
    fetchItems();
  };
  
  const handleDelete = async (itemId) => {
    if (!confirm('Delete this item?')) return;
    await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/items/${itemId}`, { method: 'DELETE', headers });
    fetchItems();
  };
  
  const handleCreateDraft = async () => {
    if (!selectedPhase) return;
    const res = await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/create-draft`, { method: 'POST', headers });
    if (res.ok) {
      const data = await res.json();
      if (data.draft) {
        setPhases(prev => [...prev, data.draft]);
        setSelectedPhase(data.draft);
      }
    }
  };
  
  const handlePromoteDraft = async () => {
    if (!selectedPhase || selectedPhase.status !== 'draft') return;
    if (!confirm('Promote this draft to active?')) return;
    await fetch(`${API}/admin/v3/phases/${selectedPhase.id}/promote`, { method: 'POST', headers });
    const res = await fetch(`${API}/admin/v3/phases`, { headers });
    if (res.ok) {
      const data = await res.json();
      setPhases(data.phases);
      const newActive = data.phases.find(p => p.id === selectedPhase.id);
      if (newActive) setSelectedPhase(newActive);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-white/40">Loading...</div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1600px] mx-auto" data-testid="schedule-editor">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90">Schedule Editor</h1>
            <p className="text-xs text-white/40 mt-1">Manage phase schedule with versioning & timeline review</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedPhase?.id || ''} onValueChange={v => setSelectedPhase(phases.find(p => p.id === v))}>
              <SelectTrigger className="w-[280px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="phase-select">
                <SelectValue placeholder="Select phase" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {phases.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white text-xs">
                    {p.phase_name} 
                    <Badge className={`ml-2 text-[9px] ${p.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : p.status === 'draft' ? 'bg-blue-500/20 text-blue-400' : 'bg-adm-surface/10 text-white/40'}`}>
                      {p.status}
                    </Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedPhase?.status === 'active' && (
              <Button onClick={handleCreateDraft} size="sm" variant="outline" className="text-xs border-white/10 text-white/70">
                <Copy className="w-3 h-3 mr-1" /> Create Draft
              </Button>
            )}
            {selectedPhase?.status === 'draft' && (
              <Button onClick={handlePromoteDraft} size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle className="w-3 h-3 mr-1" /> Promote to Active
              </Button>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-adm-surface/[0.02] border border-white/[0.06] p-1 mb-4">
            <TabsTrigger value="editor" className="data-[state=active]:bg-adm-surface/10 data-[state=active]:text-white text-white/50 text-xs px-4" data-testid="tab-editor">
              <Table2 className="w-3 h-3 mr-2" /> Table Editor
            </TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-adm-surface/10 data-[state=active]:text-white text-white/50 text-xs px-4" data-testid="tab-timeline">
              <Calendar className="w-3 h-3 mr-2" /> Timeline Review
            </TabsTrigger>
          </TabsList>
          
          {/* Table Editor Tab */}
          <TabsContent value="editor" className="mt-0">
            <ValidationPanel validation={validation} />
            <FrictionPanel friction={friction} />
            
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button onClick={handleAddItem} size="sm" className="text-xs bg-adm-surface/5 hover:bg-adm-surface/10 text-white border border-white/10" data-testid="add-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
                <Button onClick={() => { fetchItems(); fetchValidation(); fetchFriction(); }} size="sm" variant="ghost" className="text-xs text-white/50">
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
              <div className="text-xs text-white/40">{items.length} items • {items.filter(i => i.is_active).length} active</div>
            </div>
            
            <div className="border border-white/[0.06] rounded-lg overflow-hidden bg-adm-surface/[0.01]">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-adm-surface/[0.02] border-b border-white/[0.06]">
                    <tr>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">#</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Time</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Type</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Code</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Name</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Dose/Detail</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Instruction</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Context</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Block</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Group</th>
                      <th className="px-2 py-2 text-[10px] text-white/40 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <ItemRow key={item.id} item={item} onEdit={handleEdit} onDuplicate={handleDuplicate} onDelete={handleDelete} onToggleActive={handleToggleActive} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
          
          {/* Timeline Review Tab */}
          <TabsContent value="timeline" className="mt-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Select value={selectedSubscriber} onValueChange={setSelectedSubscriber}>
                  <SelectTrigger className="w-[220px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="subscriber-select">
                    <User className="w-3 h-3 mr-2 text-white/40" />
                    <SelectValue placeholder="Select subscriber" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0f] border-white/10">
                    {subscribers.map(s => (
                      <SelectItem key={s.id} value={s.id} className="text-white text-xs">
                        <span className={s.has_active_phase ? 'text-white' : 'text-white/40'}>{s.full_name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40 pointer-events-none" />
                  <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                    className="w-[150px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs pl-7" data-testid="date-input" />
                </div>
                
                <Button onClick={fetchTimeline} size="sm" variant="ghost" className="text-xs text-white/50 h-9">
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
            </div>
            
            {timelineLoading ? (
              <div className="flex items-center justify-center h-64 text-white/40">Loading timeline...</div>
            ) : !timelineData?.has_data ? (
              <div className="text-center py-12 text-white/40">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>{timelineData?.message || 'Select a subscriber and date to view timeline'}</p>
              </div>
            ) : (
              <>
                <Card className="bg-adm-surface/[0.02] border-white/[0.06] mb-4">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white/90">{timelineData.subscriber?.full_name}</div>
                        <div className="text-xs text-white/40">{timelineData.subscriber?.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-blue-500/10 text-blue-400 border-0">{timelineData.phase?.name}</Badge>
                      <div className="text-[10px] text-white/30 mt-1">
                        {new Date(timelineData.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <SummaryStrip summary={timelineData.summary} />
                
                {timelineData.blocks?.map(block => (
                  <TimelineBlock key={block.block} block={block} ruleViolations={timelineData.rule_violations} />
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
        
        <ItemEditDialog item={editItem} open={showEditDialog} onClose={() => setShowEditDialog(false)} onSave={handleSaveItem} />
      </div>
    </AdminLayout>
  );
}
